import profile from '../src/images/profile.jpeg';
import csusb from '../src/images/csusb.png';
import lpu from '../src/images/lpu.png';
import alekh from '../src/images/alekh.jpeg';
import anand from '../src/images/anand.jpeg';
import optx from '../src/images/optx.jpeg';
import rupesh from '../src/images/rupesh.jpeg';
import sathish from '../src/images/sathish.jpeg';
import snapbizz from '../src/images/snapbizz.png';
import rajesh from '../src/images/rajesh.jpeg';
import ucsb from '../src/images/ucsb.jpg';

const header = {
  homepage: '',
  title: 'SP',
};

const links = {
  calState: 'https://www.csusb.edu/',
  lpu: 'https://www.lpu.in',
  snapbizz: 'https://snapbizz.com/',
  optx: 'https://optx.com/',
  ucsb: 'https://www.ucsb.edu/',
  cv: '/research/swati-saxena-cv.pdf',
  ragEvidenceInject: 'https://github.com/swati2904/rag-evidence-inject',
  ragPaper: 'https://doi.org/10.5281/zenodo.20525655',
  ragPaperPdf: '/research/when-truth-is-retrieved-but-ignored.pdf',
  wildChatbotsPoster: '/research/wildchatbots-poster.pdf',
  webpodify: 'https://www.webpodify.com/',
  webpodcast: 'https://github.com/swati2904/webpodcast',
  trailsBuddy: 'https://github.com/swati2904/trails-buddy',
};

const about = {
  image: profile,
  name: 'Swati Saxena',
  role: 'PhD Applicant | LLM & RAG Security Researcher',
  gender: '(She/Her)',
  description:
    'I am an MS in Computer Science graduate focused on AI/ML security. My research studies how untrusted retrieved or web-sourced content can redirect language models, and how controlled benchmarks, web measurement, and lightweight defenses can make RAG and agentic systems more reliable.',
  highlights: [
    'Research interests: RAG security, indirect prompt injection, web-integrated LLM agents, adversarial evaluation',
    'First-author Zenodo preprint with released code, frozen splits, synthetic templates, and row-level logs',
    'UCSB NSF AI Institute research on large-scale LLM chatbot vulnerabilities across 3,094 websites',
  ],
  address: 'Desert Hot Springs, California, United States',
  resume: links.cv,
  social: {
    linkedin: 'https://www.linkedin.com/in/swati17/',
    github: 'https://github.com/swati2904',
  },
};

const skills = [
  'Python',
  'JavaScript',
  'TypeScript',
  'Java (OOP)',
  'SQL (PostgreSQL/MySQL)',
  'PyTorch',
  'Hugging Face Transformers',
  'Qwen2.5-7B-Instruct',
  'T5',
  'BM25 (rank_bm25)',
  'FAISS',
  'Hugging Face Datasets (KILT, NQ, HotpotQA)',
  'NumPy',
  'Pandas',
  'scikit-learn',
  'Retrieval-Augmented Generation (RAG)',
  'Indirect Prompt Injection',
  'TRIM Defense',
  'LLM-as-Judge Evaluation',
  'Rule-based ASR / EM / F1',
  'Bootstrap Confidence Intervals',
  "Cohen's κ",
  'Prompt Engineering',
  'Node.js',
  'Microservices',
  'REST',
  'GraphQL',
  'MongoDB',
  'Kafka',
  'Elastic Stack (ELK)',
  'Azure',
  'React.js',
  'Next.js',
  'Vue.js',
  'Tailwind CSS',
  'WebAssembly (WASM)',
  'Web Workers',
  'Spring Boot',
  'Docker',
  'Kubernetes',
  'Git',
  'Linux (Ubuntu)',
  'pytest',
  'LaTeX',
  'YAML Experiment Configs',
  'Jira',
  'Agile',
  'Transformer.js',
  'Readability.js',
  'Web Speech API',
  'WCAG Accessibility',
];

const educations = [
  {
    logo: csusb,
    link: links.calState,
    name: 'California State University, San Bernardino',
    degree: 'Master of Science in Computer Science',
    dates: 'May 2025',
    gpa: '3.82/4.0',
    achievements: [
      'Research focus: LLM/RAG security, adversarial evaluation, and secure web-integrated AI systems.',
      'Teaching & Instructional Student Assistant for CSE 2020 (Data Structures & Algorithms in C++) and related CS courses — lab sessions, grading, and Canvas materials aligned with WCAG/CSU accessibility standards.',
      'Participated in Grace Hopper Celebration (GHC) and Society of Women Engineers (SWE) events.',
    ],
  },
  {
    logo: lpu,
    link: links.lpu,
    name: 'Lovely Professional University',
    degree: 'Bachelor of Technology in Computer Science',
    dates: 'Jul 2020',
    gpa: '3.16/4.0',
    achievements: [
      'Top 50 teams nationwide (out of 10,000) in the Smart India Hackathon Grand Finale, endorsed by the Government of India.',
      'Volunteer for university events, coding night challenges, and Pythonthon sessions.',
    ],
  },
];

const experiences = [
  {
    company: 'Independent Research',
    logo: csusb,
    link: links.ragEvidenceInject,
    color: '#1e3a8a',
    type: 'Research',
    position: 'RAG Security & LLM Safety Researcher',
    dates: 'Mar 2026 – Present',
    address: 'San Bernardino, CA · Remote',
    description:
      'First-author Zenodo preprint on evidence-present indirect prompt injection in multi-document RAG.',
    resposibilities: [
      'Formulated an evidence-present threat model isolating obedience failure (gold passage retrieved but ignored) from retrieval failure, with a controlled benchmark over Natural Questions (KILT) and HotpotQA using sha256-stamped frozen 200-question splits.',
      'Built the full Python pipeline: dataset loaders, per-query pool construction (1 gold + 6 distractors + 1 poison), BM25 retrieval (98.5% clean gold-in-top-k recall), optional dense FAISS checks, and local Qwen2.5-7B-Instruct inference via Hugging Face Transformers.',
      'Designed TRIM (Trust-Role Isolation Middleware), reducing attack success rate from 0.283 to 0.023 on 4,595 evidence-present rows while keeping refusal <= 1% and improving EM under attack.',
      "Ran a 5,000-row factorial study (200 queries x 5 poison ranks x 5 defenses) with question-clustered bootstrap 95% CIs; validated rule-based ASR against an LLM judge (Cohen's kappa = 0.78, precision 1.00 / recall 0.71).",
    ],
    skills: [
      'Python',
      'PyTorch',
      'Hugging Face',
      'RAG',
      'BM25',
      'FAISS',
      'Qwen2.5-7B',
      'pytest',
    ],
    externalLinks: [
      {
        label: 'Read Paper',
        url: links.ragPaper,
      },
      {
        label: 'Paper PDF',
        url: links.ragPaperPdf,
      },
      {
        label: 'GitHub Repo',
        url: links.ragEvidenceInject,
      },
    ],
  },
  {
    company: 'California State University, San Bernardino',
    logo: csusb,
    link: links.calState,
    color: '#0072bc',
    type: 'Teaching',
    position: 'Teaching & Instructional Student Assistant',
    dates: 'Feb 2024 – May 2025',
    address: 'San Bernardino, CA · On-site',
    description:
      'Supported undergraduate CS instruction with labs, grading, and accessible course materials.',
    resposibilities: [
      'Assisted faculty in CSE 2020 (Data Structures & Algorithms in C++) and other computer science courses through lab sessions and programming assignment grading.',
      'Supported course delivery and Canvas materials following WCAG/CSU accessibility standards for inclusive UI design and student engagement.',
    ],
    skills: ['C++', 'Data Structures', 'Algorithms', 'WCAG', 'Canvas'],
  },
  {
    company: 'UC Santa Barbara — NSF AI Institute',
    logo: ucsb,
    link: links.ucsb,
    color: '#003660',
    type: 'Research',
    position: 'Research Intern',
    dates: 'Jun 2024 – Aug 2024',
    address: 'Santa Barbara, CA · On-site',
    description:
      'Large-scale measurement of web-deployed LLM customer-service chatbot vulnerabilities.',
    resposibilities: [
      'Built Python extraction, validation, preprocessing, logging, and integrity-check workflows for a Common Crawl/Open PageRank pipeline scanning 7.8 million hostnames.',
      'Identified 3,094 websites using 20 LLM chatbot plugins and supported analysis of fabricated chat history, client-side prompt exposure, document leakage, and poisoning through publicly modifiable content.',
      'Co-authored the Wild Chatbots research poster, connecting web-integrated chatbot vulnerabilities to later RAG-security work on untrusted retrieved content.',
    ],
    skills: ['Python', 'RAG', 'Web Security', 'Data Pipelines', 'Burp Suite'],
    externalLinks: [
      {
        label: 'Research Poster',
        url: links.wildChatbotsPoster,
      },
    ],
  },
  {
    company: 'OPTX India Private Limited',
    logo: optx,
    link: links.optx,
    color: '#ff9e00',
    type: 'Industry',
    position: 'Software Engineer',
    dates: 'Apr 2023 – Jul 2023',
    address: 'Chennai, India · On-site',
    description:
      'Node.js GraphQL/REST APIs and React/TypeScript dashboards for casino management on Azure.',
    resposibilities: [
      'Built and optimized Node.js GraphQL/REST APIs on Azure for casino management dashboards, handling real-time player data and operational analytics.',
      'Refactored React and TypeScript frontend components for a ~30% gain in operational efficiency for casino staff visualizing complex data streams.',
      'Collaborated on reliable microservices with object-oriented design for high-availability gaming infrastructure.',
    ],
    skills: ['Node.js', 'GraphQL', 'REST', 'React', 'TypeScript', 'Azure'],
  },
  {
    company: 'Snapbizz Cloudtech Pvt Ltd',
    logo: snapbizz,
    link: links.snapbizz,
    color: '#5D92F4',
    type: 'Industry',
    position: 'Software Engineer',
    dates: 'Aug 2020 – Apr 2023',
    address: 'Bengaluru, India · On-site',
    description:
      'Micro-frontend retail SaaS platform serving 1,000+ retailers with observability and reporting at scale.',
    resposibilities: [
      'Modernized the SnapOrder retail SaaS platform using a micro-frontend approach (React/Vue.js), scaling to 1,000+ retailers with Lighthouse scores exceeding 85.',
      'Developed internal observability tools for retail transaction logs and metrics, with SQL optimizations and multi-threading for peak-traffic concurrent processing.',
      'Built distributed reporting frameworks for supply chain visualization in an Agile environment.',
    ],
    skills: ['React', 'Vue.js', 'Node.js', 'SQL', 'Micro-frontends', 'Agile'],
  },
];

const publications = [
  {
    type: 'Preprint',
    title:
      'When Truth Is Retrieved but Ignored: Evidence-Present Indirect Injection in Multi-Document RAG',
    authors: 'Swati Saxena',
    venue: 'Zenodo, v1.0-preprint, June 2026',
    doi: '10.5281/zenodo.20525655',
    summary:
      'Controlled RAG security benchmark separating retrieval failure from obedience failure when gold evidence is present alongside adversarial retrieved content.',
    highlights: [
      '5,000-row factorial evaluation over 200 questions, five poison ranks, and five defense conditions using Qwen2.5-7B-Instruct.',
      'TRIM-mask reduced evidence-present attack success from 0.283 to 0.023 on 4,595 primary-analysis rows while preserving clean utility.',
      "Released code, frozen splits, synthetic templates, aggregate summaries, and row-level logs for reproducible follow-up work.",
    ],
    links: [
      {
        label: 'DOI',
        url: links.ragPaper,
        kind: 'paper',
      },
      {
        label: 'PDF',
        url: links.ragPaperPdf,
        kind: 'pdf',
      },
      {
        label: 'Code',
        url: links.ragEvidenceInject,
        kind: 'code',
      },
    ],
  },
  {
    type: 'Research Poster',
    title:
      'Wild Chatbots: Quantifying Vulnerabilities of LLM Customer Service Chatbots on the Web',
    authors:
      'Anton Landerer, Ilda Martinez, Luca Paliska, Swati Saxena, Michelle Zimmermann, Yigitcan Kaya',
    venue:
      'AI Institute for Agent-based Cyber Threat Intelligence and Operation, UC Santa Barbara, 2024',
    summary:
      'Large-scale web measurement study of security weaknesses in customer-service chatbot plugins deployed across public websites.',
    highlights: [
      'Scanned 7.8 million hostnames and identified 3,094 websites embedding 20 LLM chatbot plugins.',
      'Studied fabricated chat history, client-side system prompt exposure, document leakage, and poisoning through publicly modifiable content.',
      'Connected web-integrated chatbot vulnerabilities to later evidence-present RAG-security research.',
    ],
    links: [
      {
        label: 'Poster PDF',
        url: links.wildChatbotsPoster,
        kind: 'pdf',
      },
      {
        label: 'UCSB AI Institute',
        url: links.ucsb,
        kind: 'external',
      },
    ],
  },
];

const projects = [
  {
    name: 'RAG Evidence-Inject (TRIM)',
    badge: 'Research',
    dates: 'Mar 2026 – Present',
    description:
      'Reproducible benchmark and defense codebase for evidence-present indirect prompt injection in multi-document RAG. Includes frozen 200-question splits, TRIM/TRIM-mask defense, 5,000-row factorial evaluation logs, and LaTeX table export. Research artifact published on Zenodo.',
    stack: [
      'Python',
      'PyTorch',
      'Hugging Face Transformers',
      'Qwen2.5-7B-Instruct',
      'BM25',
      'FAISS',
      'KILT / HotpotQA',
      'pytest',
      'LaTeX',
    ],
    sourceCode: links.ragEvidenceInject,
    paperLink: links.ragPaper,
  },
  {
    name: 'WebPodify',
    badge: 'Open Source',
    dates: 'Jan 2026 – Feb 2026',
    description:
      'Chrome extension that converts any webpage into a two-person podcast-style audio summary using 100% client-side AI — no server, no data collection. Uses Qwen2.5-0.5B via Transformers.js, Readability.js extraction, multi-voice TTS, and local model caching.',
    stack: [
      'React 18',
      'Vite',
      'Transformers.js',
      'Qwen2.5-0.5B',
      'Readability.js',
      'Web Speech API',
      'WASM',
      'Chrome MV3',
    ],
    sourceCode: links.webpodcast,
    livePreview: links.webpodify,
  },
  {
    name: 'Trails Buddy',
    badge: 'Full Stack',
    dates: 'Feb 2026 – Mar 2026',
    description:
      'Hiking trail app with Spring Boot backend and React frontend. Integrates Leaflet maps, Overpass API for real-time trail data, Nominatim reverse geocoding, weather updates, user reviews, multi-language support, and caching for performance.',
    stack: [
      'React 19',
      'Spring Boot',
      'PostgreSQL/PostGIS',
      'Leaflet',
      'Overpass API',
      'Docker',
      'Kubernetes',
      'i18n',
    ],
    sourceCode: links.trailsBuddy,
  },
  {
    name: 'Safe Shortener',
    dates: '2024',
    description:
      'URL shortener with password protection, CAPTCHA, link expiry, geolocation access control, and seamless link management.',
    stack: ['Next.js', 'Docker', 'Kubernetes', 'MongoDB'],
    livePreview: 'https://safe-shortner.vercel.app/',
  },
  {
    name: 'Sync Code Editor',
    dates: '2023',
    description:
      'Real-time collaborative code editor with WebSocket sync, unique room management, and seamless room exit workflows.',
    stack: ['WebSocket', 'React', 'Redux', 'Node.js', 'Express', 'MongoDB'],
    sourceCode: 'https://github.com/swati2904/Sync_Code_Studio',
  },
];

const certificates = [
  {
    issuedBy: '',
    logo: 'oracle',
    credentialId: '',
    name: '',
    dates: '',
    description: '',
  },
];

const recommendations = [
  {
    profile: rajesh,
    name: 'Rajesh Perumal',
    linkedin: 'https://www.linkedin.com/in/rajee2686/',
    position: 'Senior Technical Manager',
    company: 'Optx',
    logo: optx,
    link: links.optx,
    description:
      'I recommend Swati as a front-end developer. Their proficiency in React & Redux, attention to detail, collaborative nature, and dedication make them a valuable addition to any team.',
  },
  {
    profile: sathish,
    name: 'Sathishkumar ThirumalaiNambi',
    linkedin: 'https://www.linkedin.com/in/sathishkumar-thirumalainambi/',
    position: 'Solution Architect',
    company: 'Optx',
    logo: optx,
    link: links.optx,
    description: `I highly recommend Swati Saxena for her exceptional skills and dedication to OPTX as a software engineer. Her expertise and commitment to delivering top-notch results have consistently impressed me. Swati is a true team player, always willing to go the extra mile to ensure success. Their professionalism, problem-solving abilities, and strong work ethic make them an invaluable asset to any organization. I have no doubt that Swati will continue to excel in their career and contribute significantly to any team they join. `,
  },
  {
    profile: rupesh,
    name: 'Rupesh Bhardwaj',
    linkedin: 'https://www.linkedin.com/in/rupesh-bhardwaj-9a222421/',
    position: 'Engineering Manager',
    company: 'Snapbizz',
    logo: snapbizz,
    link: links.snapbizz,
    description: `Swati has been working as a front-end developer at Snapbizz under my team. I was consistently impressed by her essential development skills, her dedication, and good humour. She is an innovator who always helped me to improve the productivity of my team. She is a hard worker and a talented young lady, and it was indeed a delight to work with her. Having worked with her on many projects, I must say she is a very quick learner and dedicated to her work. Her work is always outstanding, and she always goes the extra mile to ensure the company standards and client requirements are met. She is a highly skilled, experienced, and helpful professional with a positive attitude.
    Thanks, Swati.`,
  },
  {
    profile: anand,
    name: 'Anand Namastemath',
    linkedin: 'https://www.linkedin.com/in/anand-namastemath/',
    position: 'Senior Software Developer',
    company: 'Snapbizz',
    logo: snapbizz,
    link: links.snapbizz,
    description: `I had the pleasure of working closely with Swati during her time at Snapbizz, where she took on the significant responsibility of handling all front-end tasks. Swati, or as I fondly referred to her, our "brave kiddo," demonstrated a work ethic and dedication that were truly remarkable. She displayed a rare combination of rapid execution and a genuine passion for her work, consistently delivering outstanding results. Her ability to translate her interests into innovative and effective solutions was a driving force behind our project's success. Swati's contributions at Snapbizz were invaluable, and her enthusiasm and courage, as our "brave kiddo," were truly inspiring. I wholeheartedly recommend Swati for any future endeavors.`,
  },
  {
    profile: alekh,
    name: 'Alekh Kumar',
    linkedin: 'https://www.linkedin.com/in/alekh-kumar/',
    position: 'Ex-Fullstack Engineer',
    company: 'Snapbizz',
    logo: snapbizz,
    link: links.snapbizz,
    description: `Swati is like a superhero friend at work! She's super hardworking, always nice and calm, even when things are tricky. When we have problems, she's like a detective who asks the perfect questions to find answers. I remember once when we had this really tricky problem to tackle, and Swati not only cracked it but also came up with this amazing and unique idea that totally changed how we approached things. She was even the sole owner of all the Front-End projects at SnapBizz, handling them all by herself, like a true champion! And being around her is fun, she's a great friend who makes each day better with her positive vibes. I have no doubt that she will continue to shine brightly in the professional world and make a positive impact wherever she goes.`,
  },
];

const contact = {
  email: 'swatisaxena8687@gmail.com',
};

export {
  header,
  about,
  publications,
  projects,
  skills,
  educations,
  experiences,
  contact,
  recommendations,
  certificates,
  links,
};
